import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import api, { cancelAllRequests } from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import timezoneService from "../../services/TimezoneService";

const LOGOUT_IN_PROGRESS_KEY = "soluwhats:logoutInProgress";

const createNoopSocket = () => ({
  __isNoop: true,
  connected: false,
  on: () => {},
  off: () => {},
  emit: () => {},
  disconnect: () => {}
});

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(createNoopSocket());
  const [timezone, setTimezone] = useState(null);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const isLoggingOutRef = useRef(false);

  const updateCurrentUser = useCallback(nextUser => {
    setUser(currentUser => {
      const resolvedUser = typeof nextUser === "function"
        ? nextUser(currentUser)
        : nextUser;

      if (!resolvedUser) return currentUser;

      localStorage.setItem("user", JSON.stringify(resolvedUser));
      if (resolvedUser.profileImage) {
        localStorage.setItem("profileImage", resolvedUser.profileImage);
      }

      return resolvedUser;
    });
  }, []);

  const loadTimezone = async () => {
    if (!user.companyId) return;

    try {
      setTimezoneLoading(true);
      const timezoneData = await timezoneService.getCompanyTimezone();
      setTimezone(timezoneData);
    } catch (error) {
      console.error("Error loading timezone:", error);
      setTimezone({
        timezone: "America/Bogota",
        source: "fallback",
        isCustom: false
      });
    } finally {
      setTimezoneLoading(false);
    }
  };

  const reloadTimezone = () => {
    if (user.companyId) {
      loadTimezone();
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!token || !refreshToken) {
      setLoading(false);
      return;
    }

    (async () => {
      if (token && refreshToken) {
        try {
          const { data } = await api.post("/auth/refresh_token", { refreshToken });
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          localStorage.setItem("token", data.token);
          localStorage.setItem("refreshToken", data.refreshToken);
          localStorage.setItem("user", JSON.stringify(data.user));
          sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("cshow");
          localStorage.removeItem("profileImage");
          localStorage.removeItem("companyDueDate");
          localStorage.removeItem("sendSignMessage");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          setUser({});
          setSocket(createNoopSocket());
          setTimezone(null);

          if (!isLoggingOutRef.current) {
            toastError(err);
            const publicPages = ["/login", "/signup", "/forgot-password", "/reset-password"];
            if (!publicPages.includes(window.location.pathname)) {
              history.push("/login");
            }
          }
        }
      }

      setLoading(false);
    })();
  }, [history]);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      const shouldCreateSocket =
        !socket ||
        socket.__isNoop === true ||
        socket.companyId !== user.companyId ||
        socket.userId !== user.id;

      if (shouldCreateSocket) {
        io = socketConnection({ user });
        setSocket(io);
        window.socket = io;
      } else {
        io = socket;
        window.socket = io;
      }

      io.on(`company-${user.companyId}-user`, data => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
          localStorage.setItem("user", JSON.stringify(data.user));
          if (data.user.profileImage) {
            localStorage.setItem("profileImage", data.user.profileImage);
          }
        }
      });

      io.on(`company-${user.companyId}-timezone`, data => {
        if (data.action === "update") {
          reloadTimezone();
        }
      });

      loadTimezone();

      return () => {
        io.off(`company-${user.companyId}-user`);
        io.off(`company-${user.companyId}-timezone`);
      };
    }
  }, [user, socket]);

  const handleLogin = async userData => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company }
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(s => s.key === "campaignsEnabled");
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(s => s.key === "sendSignMessage");
        const signEnable = setting?.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      const dueDate =
        data.user.company.id === 1
          ? "2999-12-31T00:00:00.000Z"
          : data.user.company.dueDate;

      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      const diff = moment(dueDate).diff(moment(moment()).format());
      const before = moment(moment().format()).isBefore(dueDate);
      const dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(`Sua assinatura vence em ${Math.round(dias)} ${Math.round(dias) === 1 ? "dia" : "dias"} `);
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.\nEntre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    isLoggingOutRef.current = true;
    sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "1");

    cancelAllRequests("Logout - cancelando requisições pendentes");

    try {
      if (socket && socket.disconnect) {
        socket.disconnect();
      }
      await api.delete("/auth/logout");
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.debug("Logout finalizado com resposta nao critica.", err?.response?.status || err?.message);
      }
    } finally {
      setIsAuth(false);
      setUser({});
      setSocket(createNoopSocket());
      setTimezone(null);
      setTimezoneLoading(false);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("cshow");
      localStorage.removeItem("profileImage");
      localStorage.removeItem("companyDueDate");
      localStorage.removeItem("sendSignMessage");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);

      setTimeout(() => {
        isLoggingOutRef.current = false;
        history.push("/login");
        setTimeout(() => {
          sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
        }, 2000);
      }, 100);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        return null;
      }

      const { data } = await api.post("/auth/me", { refreshToken });
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
    timezone,
    timezoneLoading,
    loadTimezone,
    reloadTimezone,
    updateCurrentUser
  };
};

export default useAuth;
